import React, { useState, useEffect, useRef } from 'react';
import { Shield, Award, Heart, Clock, Users, Globe } from 'lucide-react';

interface Advantage {
  id: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const advantages: Advantage[] = [
  {
    id: 1,
    icon: <Shield className="w-7 h-7" />,
    title: 'Надежность',
    description: 'Работаем с 2015 года и помогли более 10 000 туристов организовать незабываемый отдых.'
  },
  {
    id: 2,
    icon: <Award className="w-7 h-7" />,
    title: 'Качество',
    description: 'Индивидуальный подход к каждому клиенту и высокие стандарты сервиса.'
  },
  {
    id: 3,
    icon: <Heart className="w-7 h-7" />,
    title: 'Забота',
    description: 'Поддержка 24/7 до, во время и после путешествия для вашего спокойствия.'
  },
  {
    id: 4,
    icon: <Clock className="w-7 h-7" />,
    title: 'Экономия времени',
    description: 'Берем на себя все заботы по организации вашего отдыха от начала до конца.'
  },
  {
    id: 5,
    icon: <Users className="w-7 h-7" />,
    title: 'Эксперты',
    description: 'Наши менеджеры регулярно посещают курорты и знают все нюансы отдыха.'
  },
  {
    id: 6,
    icon: <Globe className="w-7 h-7" />,
    title: 'Широкий выбор',
    description: 'Сотрудничество с ведущими туроператорами для лучших предложений.'
  }
];

interface StatProps {
  end: number;
  suffix: string;
  label: string;
}

const AnimatedStat: React.FC<StatProps> = ({ end, suffix, label }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, end]);

  return (
    <div ref={ref} className="text-center p-8">
      <div className="text-4xl md:text-5xl font-bold text-white mb-2">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-white/80 text-lg">{label}</div>
    </div>
  );
};

const AboutUs: React.FC = () => {
  return (
    <section id="about" className="py-20 bg-neutral-50">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-block bg-primary-500/10 text-primary-500 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
              О компании
            </div>
            <h2 className="section-title mb-8">Hasan Travel — ваш надежный гид в мире путешествий</h2>
            <p className="text-neutral-600 text-lg mb-6 leading-relaxed">
              ТОО "Hasan Tour" — туристическое агентство, которое с 2015 года помогает казахстанцам организовать идеальный отдых в любой точке мира. Мы стремимся сделать путешествия доступными, комфортными и незабываемыми.
            </p>
            <p className="text-neutral-600 text-lg mb-6 leading-relaxed">
              Наша миссия — дарить людям радость открытий новых стран, культур и впечатлений, обеспечивая высочайший уровень сервиса на всех этапах организации путешествия.
            </p>
            <p className="text-neutral-600 text-lg mb-8 leading-relaxed">
              Мы сотрудничаем только с проверенными партнерами — международными туроператорами, отелями и транспортными компаниями.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#contacts" className="btn btn-primary">
                Связаться с нами
              </a>
              <a href="https://wa.me/77019823126" target="_blank" rel="noopener noreferrer" className="btn bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-500/25">
                Написать в WhatsApp
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {advantages.map((advantage) => (
              <div
                key={advantage.id}
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-neutral-100 group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                  {advantage.icon}
                </div>
                <h3 className="text-lg font-bold text-neutral-800 mb-2">
                  {advantage.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {advantage.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-20 bg-gradient-to-r from-primary-500 to-primary-700 rounded-3xl overflow-hidden shadow-2xl shadow-primary-500/20">
          <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/10">
            <AnimatedStat end={8} suffix="+" label="лет на рынке" />
            <AnimatedStat end={10000} suffix="+" label="довольных клиентов" />
            <AnimatedStat end={45} suffix="+" label="направлений" />
            <AnimatedStat end={50} suffix="+" label="партнеров" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
